<template>
	<form class="new-ticket-form" @submit.prevent="createTicket()">
		<section class="request-form-section">
			<div class="request-field-grid">
				<portal-select-field
					v-model="ticketType"
					field-class="request-type-field"
					:label="$t('requests.new.type')"
					:options="ticketTypeOptions"
				></portal-select-field>

				<label class="request-text-field">
					<span>{{ $t('requests.new.subject') }}</span>
					<input v-model="subject" type="text" :aria-label="$t('requests.new.subject')" />
				</label>

				<contact-selector
					class="request-contact-field"
					:to-list="ccList"
					:contacts="contacts"
					:label="$t('requests.new.cc')"
					@update="updateCcList"
				></contact-selector>
			</div>
		</section>

		<section class="request-form-section request-details-section">
			<div class="request-dynamic-form" v-if="selectedType.discoveryTemplate" :style="requestFormStyle">
				<ticket-form-v1
					ref="form"
					:key="selectedType.discoveryTemplate.id"
					v-if="selectedType.discoveryTemplate.schemaVersion == 1"
					:template="selectedType.discoveryTemplate"
				></ticket-form-v1>
				<ticket-form-v2
					ref="form"
					:key="selectedType.discoveryTemplate.id"
					v-if="selectedType.discoveryTemplate.schemaVersion == 2"
					:template="selectedType.discoveryTemplate"
				></ticket-form-v2>
			</div>

			<label class="request-textarea-field" v-else>
				<span>{{ $t('requests.new.details') }}</span>
				<textarea
					v-model="comment"
					rows="8"
					:aria-label="$t('requests.new.details')"
				></textarea>
			</label>
		</section>

		<portal-file-input
			v-model="attachments"
			:label="$t('requests.new.attachments')"
			:placeholder="$t('requests.new.upload-attachments')"
			:remove-label="$t('global.delete')"
		></portal-file-input>

		<footer class="request-form-actions">
			<button type="button" class="cancel-request-action" @click="confirmCancel()">
				{{ $t('global.cancel') }}
			</button>
			<button type="submit" class="submit-request-action">
				{{ $t('requests.new.submit') }}
			</button>
		</footer>
	</form>
</template>

<script>
	import ClientService from '@/modules/client/ClientService';
	import ConfirmModal from '@/components/ConfirmModal';
	import TicketService from '@/modules/tickets/TicketService';
	import ContactSelector from '@/modules/tickets/ContactSelector';
	import TicketFormV1 from '@/modules/tickets/TicketFormV1';
	import TicketFormV2 from '@/modules/tickets/TicketFormV2';
	import PortalFileInput from '@/components/PortalFileInput';
	import PortalSelectField from '@/components/filters/PortalSelectField';
	import { resolveDocumentBranding, ticketFormBranding } from '@/utils/portalBranding';
	export default {
		name: 'NewTicket',

		props: ['ticketTypes'],

		components: { TicketFormV1, TicketFormV2, ContactSelector, PortalFileInput, PortalSelectField },

		data: function() {
			return {
				clientService: new ClientService(),
				ticketService: new TicketService(),
				subject: null,
				ticketType: this.ticketTypes[0].id,
				comment: null,
				attachments: [],
				contacts: [],
				ccList: [],
				formResult: null,
			};
		},

		mounted() {
			this.clientService.getContactList().then((res) => {
				this.contacts.splice(0, this.contacts.length);
				this.contacts.push(
					...res.data.filter(
						(c) => c.email && c.email.toLowerCase() !== this.$store.state.loggedInUser.email.toLowerCase()
					)
				);
			});
		},

		beforeDestroy() {},

		methods: {
			validate: function() {
				if (!this.subject) {
					this.$store.commit('error', this.$t('error.subject-required'));
					return false;
				}

				if (this.$refs.form) {
					if (!this.$refs.form.validate()) {
						this.$store.commit('error', this.$t('error.correct-errors'));
						return false;
					}
				} else if (!this.comment) {
					this.$store.commit('error', this.$t('error.request-details'));
					return false;
				}

				return true;
			},

			createTicket: function() {
				try {
					if (!this.validate()) {
						this.$store.commit('error', 'Please correct the errors in your form.');
						return;
					}

					let newTicket = {
						subject: this.subject,
						comment: this.comment,
						ticketType: this.ticketType,
						ccList: this.ccList,
					};

					if (this.selectedType.discoveryTemplate) {
						let formData = {};
						this.$refs.form.setFormResult(formData);

						let files = formData.files;
						delete formData.files;
						this.attachments.push(...files);

						newTicket.formData = formData;
					}

					this.$store.commit('startLoading');
					this.ticketService
						.createTicket(newTicket, this.attachments)
						.then((res) => {
							this.$emit('new-ticket', res.data.ticket.id);
						})
						.catch((err) => this.$store.commit('error', err.response.data.message))
						.finally(() => this.$store.commit('stopLoading'));
				} catch (err) {
					console.log(err);
					this.$store.commit('error', JSON.stringify(err));
				}
			},

			updateCcList: function(list) {
				this.ccList.splice(0, this.ccList.length);
				this.ccList.push(...list);
			},

			handleResult(result) {
				this.formResult = result;
			},

			confirmCancel: function() {
				if (this.comment || this.subject || this.attachments.length) {
					let binding = {
						bodyText: this.$t('requests.confirm-cancel'),
					};
					this.$store.state.globalModalController.openModal(ConfirmModal, binding).then((res) => {
						if (res) {
							setTimeout(() => this.$emit('cancel'), 200);
						}
					});
				} else {
					this.$emit('cancel');
				}
			},
		},

		computed: {
			ticketTypeOptions: function() {
				return this.ticketTypes.map((ticketType) => ({
					text: ticketType.type,
					value: ticketType.id,
				}));
			},

			selectedType: function() {
				return this.ticketTypes.find((t) => t.id === this.ticketType);
			},

			requestFormStyle: function() {
				if (this.selectedType.discoveryTemplate.schemaVersion != 2) return null;
				const branding = resolveDocumentBranding(
					ticketFormBranding(this.selectedType.discoveryTemplate, this.$store.state.domain),
					this.$vuetify.theme.dark ? 'dark' : 'light'
				);
				return branding ? { backgroundColor: branding.colors.background } : null;
			},
		},
	};
</script>

<style scoped lang="scss">
	.new-ticket-form {
		display: grid;
		gap: 16px;
	}

	.request-form-section {
		min-width: 0;
	}

	.request-field-grid {
		display: grid;
		grid-template-columns: minmax(0, 1fr);
		gap: 12px;
	}

	.request-type-field,
	.request-contact-field {
		min-width: 0;
	}

	.new-ticket-form ::v-deep .request-type-field {
		width: 100%;
		height: 48px;
		min-width: 0;
		flex-basis: auto;
		background: var(--brand-field-fill, var(--v-gray_10-base));
	}

	.request-text-field {
		min-width: 0;
		height: 48px;
		display: flex;
		align-items: center;
		gap: 9px;
		padding: 0 12px;
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-field-radius, 12px);
		background: var(--brand-field-fill, var(--v-gray_10-base));
		transition: border-color 140ms ease, background-color 140ms ease, box-shadow 140ms ease;
	}

	.request-text-field:focus-within,
	.request-textarea-field:focus-within {
		border-color: var(--v-primary-base);
		background: var(--brand-field-fill, var(--portal-surface));
		box-shadow: 0 0 0 3px color-mix(in srgb, var(--v-primary-base) 10%, transparent);
	}

	.request-text-field > span {
		flex: 0 0 auto;
		color: var(--portal-muted);
		font-size: 12px;
		font-weight: 600;
	}

	.request-text-field input {
		min-width: 0;
		flex: 1;
		border: 0;
		outline: 0;
		background: transparent;
		color: var(--portal-text);
		font: inherit;
		font-size: 14px;
	}

	.request-details-section {
		padding-top: 2px;
	}

	.request-textarea-field {
		display: grid;
		gap: 8px;
		padding: 12px;
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-field-radius, 12px);
		background: var(--brand-field-fill, var(--v-gray_10-base));
		transition: border-color 140ms ease, background-color 140ms ease, box-shadow 140ms ease;
	}

	.request-textarea-field > span {
		color: var(--portal-text);
		font-size: 12px;
		font-weight: 680;
	}

	.request-textarea-field textarea {
		width: 100%;
		min-height: 150px;
		padding: 0;
		border: 0;
		outline: 0;
		resize: vertical;
		background: transparent;
		color: var(--portal-text);
		font: inherit;
		font-size: 14px;
		line-height: 1.55;
	}

	.request-dynamic-form {
		padding: 14px;
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-surface-radius, 12px);
		background: var(--brand-background, var(--v-gray_10-base));
	}

	.request-dynamic-form ::v-deep > .px-2 {
		padding: 0 !important;
	}

	.request-dynamic-form ::v-deep .v-text-field > .v-input__control > .v-input__slot {
		border-radius: var(--brand-field-radius, 12px) !important;
		background: var(--portal-surface);
	}

	.request-dynamic-form ::v-deep fieldset {
		border-color: var(--portal-border);
	}

	.request-form-actions {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 9px;
		padding-top: 2px;
	}

	.cancel-request-action,
	.submit-request-action {
		min-height: 40px;
		padding: 0 15px;
		border-radius: var(--brand-button-radius, 10px);
		font: inherit;
		font-size: 12px;
		font-weight: 680;
		cursor: pointer;
	}

	.cancel-request-action {
		border: 1px solid var(--portal-border);
		background: var(--portal-surface);
		color: var(--portal-muted);
	}

	.cancel-request-action:hover {
		background: var(--v-gray_20-base);
		color: var(--portal-text);
	}

	.submit-request-action {
		min-width: 146px;
		border: 0;
		background: var(--v-primary-base);
		color: var(--v-white-base);
		box-shadow: 0 5px 14px color-mix(in srgb, var(--v-primary-base) 18%, transparent);
	}

	.cancel-request-action:focus-visible,
	.submit-request-action:focus-visible {
		outline: 2px solid var(--v-primary-base);
		outline-offset: 2px;
	}

	@media (max-width: 599px) {
		.request-form-actions {
			align-items: center;
		}

		.submit-request-action {
			min-width: 0;
			flex: 1;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.request-text-field,
		.request-textarea-field {
			transition: none;
		}
	}
</style>
